import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, X, Loader2, SwitchCamera, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { sourceToJpegFile } from "@/lib/imagePrep";

/**
 * In-app camera (getUserMedia). The page never leaves the browser process, so
 * Android cannot kill the TWA while a separate camera app is in the foreground
 * (the cause of "Not enough memory to complete the previous operation" and the
 * lost photo). The captured frame is downscaled to <= 2000 px and handed to
 * `onCapture(file)` as a normal JPEG File -> same pipeline as gallery/file.
 *
 * Photos taken here are NOT written to the device gallery; the UI says so.
 */
export function CameraCapture({ open, onCapture, onCancel, onUseNativeCamera }) {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus] = useState("starting"); // starting | ready | capturing | error
  const [errorCode, setErrorCode] = useState(null);
  const [facing, setFacing] = useState("environment");
  const [canFlip, setCanFlip] = useState(false);

  const stop = useCallback(() => {
    const s = streamRef.current;
    if (s) { s.getTracks().forEach((tr) => { try { tr.stop(); } catch {} }); }
    streamRef.current = null;
    if (videoRef.current) { try { videoRef.current.srcObject = null; } catch {} }
  }, []);

  const start = useCallback(async (mode) => {
    stop();
    setStatus("starting"); setErrorCode(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: mode }, width: { ideal: 2560 }, height: { ideal: 1920 } },
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) { stop(); return; }
      v.srcObject = stream;
      // Never hang on odd devices/streams: wait for metadata OR a short timeout,
      // and race play() against a timeout as well. The shutter re-checks that
      // frames exist (videoWidth > 0) before capturing.
      await new Promise((resolve) => {
        if (v.readyState >= 2) return resolve();
        v.onloadedmetadata = () => resolve();
        setTimeout(resolve, 2500);
      });
      await Promise.race([v.play().catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
      setStatus("ready");
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setCanFlip(devices.filter((d) => d.kind === "videoinput").length > 1);
      } catch { setCanFlip(false); }
    } catch (e) {
      setErrorCode(e?.name || "CameraError");
      setStatus("error");
    }
  }, [stop]);

  useEffect(() => {
    if (!open) { stop(); return undefined; }
    start(facing);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { stop(); document.body.style.overflow = prevOverflow; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const capture = async () => {
    const v = videoRef.current;
    if (!v || status !== "ready") return;
    if (!v.videoWidth) {
      // Stream attached but no frames yet -> let the user retry instead of a dead button.
      setErrorCode("CaptureFailed");
      setStatus("error");
      return;
    }
    setStatus("capturing");
    try {
      const file = await sourceToJpegFile(v, `paragon-${Date.now()}.jpg`);
      stop();
      onCapture(file);
    } catch {
      setErrorCode("CaptureFailed");
      setStatus("error");
    }
  };

  if (!open) return null;

  const errorText = errorCode === "NotAllowedError" || errorCode === "SecurityError"
    ? t("scan.cam.errPermission")
    : errorCode === "NotFoundError" || errorCode === "OverconstrainedError"
      ? t("scan.cam.errNoCamera")
      : t("scan.cam.errGeneric");

  return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" data-testid="camera-capture" role="dialog" aria-modal="true" aria-label={t("scan.cam.title")}>
      {/* top bar */}
      <div className="flex items-center justify-between px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 text-white">
        <div>
          <p className="font-display font-semibold">{t("scan.cam.title")}</p>
          <p className="text-xs text-white/60">{t("scan.cam.hint")}</p>
        </div>
        <Button data-testid="camera-cancel-button" variant="ghost" size="icon" onClick={() => { stop(); onCancel(); }} className="text-white hover:bg-white/10 h-11 w-11" aria-label={t("scan.cancel")}>
          <X style={{ width: 22, height: 22 }} />
        </Button>
      </div>

      {/* viewfinder */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 h-full w-full object-cover" data-testid="camera-video" />
        {status === "ready" && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative w-[78%] max-w-[420px] aspect-[3/4] max-h-[80%]">
              <span className="scan-effect-corner tl" /><span className="scan-effect-corner tr" />
              <span className="scan-effect-corner bl" /><span className="scan-effect-corner br" />
            </div>
          </div>
        )}
        {status === "starting" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80 gap-3" data-testid="camera-starting">
            <Loader2 className="animate-spin text-cyan-300" style={{ width: 28, height: 28 }} />
            <p className="text-sm">{t("scan.cam.starting")}</p>
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-4" data-testid="camera-error">
            <AlertTriangle className="text-amber-300" style={{ width: 30, height: 30 }} />
            <p className="text-white text-sm">{errorText}</p>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              {onUseNativeCamera && (
                <Button data-testid="camera-use-native-button" onClick={() => { stop(); onUseNativeCamera(); }} className="bg-cyan-400 text-slate-900 hover:bg-cyan-300 font-semibold h-11">
                  {t("scan.cam.useNative")}
                </Button>
              )}
              <Button data-testid="camera-retry-button" variant="outline" onClick={() => start(facing)} className="border-white/20 bg-white/5 text-white hover:bg-white/10 h-11">
                {t("billing.retry")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* controls */}
      <div className="px-6 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] flex items-center justify-between text-white">
        <Button data-testid="camera-cancel-text-button" variant="ghost" onClick={() => { stop(); onCancel(); }} className="text-white/80 hover:bg-white/10 h-11">
          {t("scan.cancel")}
        </Button>
        <button
          type="button"
          data-testid="camera-shutter-button"
          onClick={capture}
          disabled={status !== "ready"}
          aria-label={t("scan.cam.shutter")}
          className="h-[72px] w-[72px] rounded-full border-4 border-white/90 bg-cyan-400 flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
        >
          {status === "capturing" ? <Loader2 className="animate-spin text-slate-900" style={{ width: 26, height: 26 }} /> : <Camera className="text-slate-900" style={{ width: 28, height: 28 }} />}
        </button>
        <Button
          data-testid="camera-flip-button"
          variant="ghost"
          size="icon"
          disabled={!canFlip || status !== "ready"}
          onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
          className="text-white hover:bg-white/10 h-11 w-11 disabled:opacity-30"
          aria-label={t("scan.cam.flip")}
        >
          <SwitchCamera style={{ width: 22, height: 22 }} />
        </Button>
      </div>
      <p className="text-center text-[11px] text-white/50 pb-3 px-6">{t("scan.cam.notSaved")}</p>
    </div>
  );
}
